import { NoneLabel } from '@/components/labels/NoneLabel';
import { HorizontalStack, VerticalStack } from '@/components/utils/linearLayouts';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import Edit from '@mui/icons-material/Edit';
import { Button, IconButton, TextField, Typography } from '@mui/material';
import { useState } from 'react';

import { type Children, type ReturnComponent } from '../../utils/reactTypes';
import { NotesInfoIcon } from './NotesInfoIcon';

const EditCancelButtons = ({
    isEditing,
    onCancel,
    onSubmit,
    onEdit,
    disableSave,
    iconActions = false,
}: {
    isEditing: boolean;
    onCancel: () => void;
    onSubmit: () => void;
    onEdit: () => void;
    disableSave: boolean;
    iconActions?: boolean;
}): ReturnComponent => {
    if (iconActions) {
        return isEditing ? (
            <HorizontalStack gap='2px' alignItems='center' fullWidth={false}>
                <IconButton size='small' aria-label='Cancel' onClick={onCancel}>
                    <Close fontSize='small' />
                </IconButton>
                <IconButton
                    size='small'
                    color='primary'
                    aria-label='Save'
                    disabled={disableSave}
                    onClick={onSubmit}
                >
                    <Check fontSize='small' />
                </IconButton>
            </HorizontalStack>
        ) : (
            <IconButton size='small' aria-label='Edit' onClick={onEdit}>
                <Edit fontSize='small' />
            </IconButton>
        );
    }
    return isEditing ? (
        <HorizontalStack gap='2px' alignItems='center' fullWidth={false}>
            <Button
                size='small'
                variant='text'
                color='primary'
                sx={{ textTransform: 'none' }}
                onClick={onCancel}
            >
                Cancel
            </Button>
            <Button
                size='small'
                variant='contained'
                disabled={disableSave}
                sx={{
                    textTransform: 'none',
                    minWidth: '45px',
                    padding: '2px 5px',
                }}
                onClick={onSubmit}
            >
                Save
            </Button>
        </HorizontalStack>
    ) : (
        <Button
            size='small'
            variant='text'
            color='primary'
            sx={{ textTransform: 'none' }}
            onClick={onEdit}
        >
            Edit
        </Button>
    );
};

export interface NotesEditorProps {
    notes: string | null;
    canView: boolean;
    canEdit: boolean;
    onSave: (notes: string) => Promise<void>;
    notesInfoIconProps: {
        visibleToClinicians: boolean;
        headline: string;
        description: string;
    };
    captionLabel: Children;
    transformNotesBeforeSave?: (notes: string) => string | null;
    iconActions?: boolean;
}

export const NotesEditor = ({
    notes,
    canView,
    canEdit,
    onSave,
    notesInfoIconProps,
    captionLabel,
    transformNotesBeforeSave,
    iconActions,
}: NotesEditorProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const saved = notes ?? '';
    const disableSave = draft === saved || isSaving;

    const onEdit = () => {
        setDraft(saved);
        setIsEditing(true);
    };

    const onCancel = () => {
        setIsEditing(false);
    };

    const onSubmit = async () => {
        if (draft === saved) {
            return;
        }

        setIsSaving(true);
        try {
            const notesToSave = transformNotesBeforeSave ? transformNotesBeforeSave(draft) : draft;
            await onSave(notesToSave ?? '');
            setIsEditing(false);
        } finally {
            setIsSaving(false);
        }
    };

    if (!canView) {
        return null;
    }

    return (
        // No spacing because the caption header ends up being taller than in other left rail sections.
        <VerticalStack spacing={0}>
            <HorizontalStack>
                <NotesInfoIcon {...notesInfoIconProps}>{captionLabel}</NotesInfoIcon>
                {canEdit ? (
                    <EditCancelButtons
                        isEditing={isEditing}
                        onCancel={onCancel}
                        onSubmit={() => {
                            void onSubmit();
                        }}
                        onEdit={onEdit}
                        disableSave={disableSave}
                        iconActions={iconActions}
                    />
                ) : undefined}
            </HorizontalStack>
            {isEditing ? (
                <TextField
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    autoFocus
                    multiline
                    fullWidth
                    variant='outlined'
                    size='small'
                    InputProps={{
                        sx: {
                            fontSize: '14px',
                            lineHeight: '20px',
                            backgroundColor: 'transparent',
                        },
                    }}
                />
            ) : saved ? (
                <Typography
                    variant='body2'
                    sx={{
                        color: 'grey.600',
                        fontSize: '14px',
                        lineHeight: '20px',
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'anywhere',
                    }}
                >
                    {saved}
                </Typography>
            ) : (
                <NoneLabel sx={{ fontSize: '14px', lineHeight: '20px' }} />
            )}
        </VerticalStack>
    );
};
